import { useState } from "react";
import { Text, Group, Stack, Paper, Box, Flex, ScrollArea, Title, Modal } from "@mantine/core";
import {
  IconDeviceDesktop,
  IconDeviceMobile,
  IconInfoCircle,
  IconMessage2,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { Button, Stepper, ToggleSwitch, Tooltip } from "@onengine/component-library";
import clsx from "clsx";
import { usePreviewForm } from "@/hooks/form-builder/usePreviewForm";
import { useShallow } from "zustand/react/shallow";
import { useFormStore } from "@/store/form-builder";
import { useAssistantStore } from "@/store/form-builder/useAssistantStore";
import { isQuestionVisible } from "@/components/features/FormBuilder/utils/evaluateLogic";
import {
  NO_PROVIDER_TYPE,
  PROVIDER_TYPE_OPTIONS,
} from "@/components/features/FormBuilder/data/contextTriggers";
import { BUILDER_LABELS } from "@/components/features/FormBuilder/data/builderLabels";
import InputDropdown from "@/components/ui/Input/InputDropdown";
import { SignatureCanvas } from "@/components/features/FormBuilder/components/Wrappers/Signature/SignatureCanvas";
import { PreviewQuestion } from "@/components/features/FormBuilder/components/Preview/PreviewQuestion";
import { useEmptyQuestionsGuard } from "@/components/features/FormBuilder/components/Shared/useEmptyQuestionsGuard";
import { PublishConfirmModal } from "@/components/features/FormBuilder/components/Shared/PublishConfirmModal";
import { AssistantDock } from "@/components/features/FormBuilder/Assistant/AssistantDock";
import { AssistantPanel } from "@/components/features/FormBuilder/Assistant/AssistantPanel";
import { ProposalBanner } from "@/components/features/FormBuilder/Assistant/ProposalBanner";
import { useFormAssistantAccess } from "@/components/features/FormBuilder/Assistant/useFormAssistantAccess";
import { useIsNarrowEditor } from "@/components/features/FormBuilder/hooks/useIsNarrowEditor";
import styles from "@/components/features/FormBuilder/components/Preview/PreviewMode.module.scss";
import assistantStyles from "@/components/features/FormBuilder/Assistant/Assistant.module.scss";

interface PreviewModeProps {
  tenantId?: string;
  readOnly?: boolean;
  /** Publishing from the preview lets the person test the form and publish it in one place. */
  onPublish?: () => void | Promise<void>;
  isPublishing?: boolean;
  confirmPublish?: boolean;
}

const PREVIEW_AS_ITEMS = PROVIDER_TYPE_OPTIONS.map((option) =>
  option.value === NO_PROVIDER_TYPE
    ? { ...option, label: BUILDER_LABELS.previewAnyProvider }
    : option,
);

const PREVIEW_AS_HINT =
  "See the form as this type of service provider would, with their logic applied.";
const VALIDATION_HINT =
  "On: required fields are checked when you press Next or Submit, like in the real form.";

function HintLabel({ id, hint, children }: { id?: string; hint: string; children: string }) {
  return (
    <Tooltip content={hint}>
      <div className={styles.hintLabel}>
        <Text id={id} size="sm" className={styles.headerTitle}>
          {children}
        </Text>
        <IconInfoCircle size={14} className={styles.hintIcon} aria-hidden />
      </div>
    </Tooltip>
  );
}

export function PreviewMode({
  tenantId,
  readOnly,
  onPublish,
  isPublishing,
  confirmPublish,
}: PreviewModeProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isNarrow = useIsNarrowEditor();
  const { guard: guardEmptyQuestions, dialog: emptyQuestionsDialog } = useEmptyQuestionsGuard();
  const assistantEnabled = useFormAssistantAccess() && !readOnly;
  const { isOpen, proposal, stale, isBusy, setOpen, discardProposal, applyProposal } =
    useAssistantStore(
      useShallow((s) => ({
        isOpen: s.isOpen,
        proposal: s.proposal,
        stale: s.stale,
        isBusy: s.phase === "thinking" || s.phase === "building",
        setOpen: s.setOpen,
        discardProposal: s.discardProposal,
        applyProposal: s.applyProposal,
      })),
    );
  const initFromJSON = useFormStore((s) => s.initFromJSON);
  const draftQuestionCount = useFormStore((s) =>
    s.pages.reduce(
      (total, page) =>
        total + page.sections.reduce((sum, section) => sum + section.questions.length, 0),
      0,
    ),
  );
  const showAssistant = assistantEnabled && isOpen;
  const visibleProposal = assistantEnabled ? proposal : null;

  const handleApplyProposal = () => {
    const pages = applyProposal(useFormStore.getState().getCompactJSON());
    if (pages) initFromJSON(pages);
  };

  const {
    control,
    errors,
    clearErrors,
    withValidation,
    setWithValidation,
    previewProviderType,
    setPreviewProviderType,
    curPage,
    curIdx,
    isLastPage,
    stepperSteps,
    viewMode,
    setViewMode,
    evalResult,
    sigModal,
    setSigModal,
    submitted,
    setIsPreview,
    handleNext,
    handlePrevious,
    handleFormSubmit,
    handleStepChange,
    handleSignatureConfirm,
    handleReset,
  } = usePreviewForm(visibleProposal?.pages);

  const numberedSteps = stepperSteps.map((step, index) => ({
    ...step,
    label: `${index + 1} · ${step.label}`,
  }));

  if (submitted) {
    return (
      <Flex className={styles.submittedPage}>
        <Paper radius="lg" withBorder p="xl" maw={480} w="100%" ta="center">
          <Stack gap="md" align="center">
            <Box className={styles.successIconWrap}>✓</Box>
            <Title order={2} className={styles.successTitle}>
              Form Submitted
            </Title>
            <Text size="md" className={styles.successSubtitle}>
              Your responses have been recorded successfully.
            </Text>
            <Group gap="sm" mt="md">
              <Button variant="secondary" size="base" onClick={() => setIsPreview(false)}>
                Back to Editor
              </Button>
              <Button variant="primary" size="base" onClick={handleReset}>
                Submit Another
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Flex>
    );
  }

  return (
    <Flex className={styles.previewPage}>
      <Box className={styles.headerBar}>
        <div className={styles.headerInner}>
          <Text size="xl" className={styles.headerTitle}>
            Form Preview
          </Text>
          <div className={styles.settings}>
            <Group gap="xs" data-testid="preview-as" wrap="nowrap">
              <HintLabel id="preview-as-label" hint={PREVIEW_AS_HINT}>
                Preview as
              </HintLabel>
              <InputDropdown
                value={previewProviderType}
                onChange={(value) => value && setPreviewProviderType(value)}
                items={PREVIEW_AS_ITEMS}
                size="small"
                labelledBy="preview-as-label"
                className={styles.previewAsDropdown}
              />
            </Group>
            <Group gap="xs" wrap="nowrap" className={styles.validationControl}>
              <HintLabel hint={VALIDATION_HINT}>Show required-field errors</HintLabel>
              <ToggleSwitch
                id="toggle-validation"
                checked={withValidation}
                onChange={setWithValidation}
                size="sm"
                intent="brand"
              />
            </Group>
            {assistantEnabled && (
              <Button
                variant={isOpen ? "secondary" : "tertiary"}
                size="small"
                iconLeft={<IconMessage2 size={16} />}
                onClick={() => setOpen(!isOpen)}
                htmlAttributes={{ "aria-pressed": isOpen }}
              >
                Assistant
              </Button>
            )}
            <Paper radius="md" withBorder className={styles.viewTogglePaper}>
              <Button
                type="icon"
                variant={viewMode === "desktop" ? "primary" : "tertiary"}
                size="small"
                iconLeft={<IconDeviceDesktop size={18} />}
                onClick={() => setViewMode("desktop")}
                htmlAttributes={{ "aria-label": "Desktop view" }}
              />
              <Button
                type="icon"
                variant={viewMode === "mobile" ? "primary" : "tertiary"}
                size="small"
                iconLeft={<IconDeviceMobile size={18} />}
                onClick={() => setViewMode("mobile")}
                htmlAttributes={{ "aria-label": "Phone view" }}
              />
            </Paper>
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" size="small" onClick={() => setIsPreview(false)}>
              Exit Preview
            </Button>
            {onPublish && !readOnly && (
              <Tooltip
                content="Apply or discard the assistant's proposal first"
                disabled={!visibleProposal}
              >
                <div>
                  <Button
                    variant="primary"
                    size="small"
                    iconLeft={<IconPlayerPlay size={14} fill="white" />}
                    onClick={() =>
                      guardEmptyQuestions(() =>
                        confirmPublish ? setConfirmOpen(true) : void onPublish(),
                      )
                    }
                    isDisabled={isBusy || !!visibleProposal || !!isPublishing}
                  >
                    {isPublishing ? "Publishing..." : "Publish"}
                  </Button>
                </div>
              </Tooltip>
            )}
          </div>
          {onPublish && emptyQuestionsDialog}
          {onPublish && (
            <PublishConfirmModal
              isOpen={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              onConfirm={() => {
                setConfirmOpen(false);
                void onPublish();
              }}
            />
          )}
        </div>
      </Box>

      <div className={styles.body}>
        <ScrollArea className={styles.scrollArea} p="lg">
          <Stack
            gap="lg"
            maw={viewMode === "mobile" ? 375 : isNarrow ? "100%" : "65%"}
            w="100%"
            mx="auto"
          >
            {visibleProposal && (
              <ProposalBanner
                isStale={stale}
                isBusy={isBusy}
                replacedQuestionCount={draftQuestionCount}
                onDiscard={discardProposal}
                onApply={handleApplyProposal}
              />
            )}
            {numberedSteps.length > 1 && (
              <Paper radius="lg" withBorder p="md" className={styles.stepperPaper}>
                <Stepper
                  steps={numberedSteps}
                  activeStepId={curPage?.id || stepperSteps[0]?.id}
                  onStepChange={handleStepChange}
                  isMobile={viewMode === "mobile"}
                />
              </Paper>
            )}

            {curPage?.sections
              .filter((sc) => !evalResult.hiddenSections.has(sc.id))
              .map((sc) => (
                <Paper
                  key={sc.id}
                  radius="lg"
                  withBorder
                  p={viewMode === "mobile" ? "md" : "xl"}
                  className={clsx(
                    styles.sectionPaper,
                    visibleProposal && assistantStyles.proposedSection,
                  )}
                >
                  <Text size="lg" className={styles.sectionTitle}>
                    {sc.name}
                  </Text>
                  {sc.description && (
                    <Text size="sm" className={styles.sectionDescription} mb="lg">
                      {sc.description}
                    </Text>
                  )}
                  <Stack gap="lg">
                    {sc.questions.filter((qq) => isQuestionVisible(qq.id, qq, evalResult))
                      .length === 0 ? (
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        No questions in this section yet.
                      </Text>
                    ) : (
                      sc.questions
                        .filter((qq) => isQuestionVisible(qq.id, qq, evalResult))
                        .map((qq) => (
                          <PreviewQuestion
                            key={qq.id}
                            question={qq}
                            control={control}
                            evalResult={evalResult}
                            error={
                              withValidation
                                ? (errors[qq.id]?.message as string | undefined)
                                : undefined
                            }
                            onSig={setSigModal}
                            clearErrors={clearErrors}
                            isMobile={viewMode === "mobile"}
                          />
                        ))
                    )}
                  </Stack>
                </Paper>
              ))}

            <Group
              justify={curIdx > 0 ? "space-between" : "flex-end"}
              grow={viewMode === "mobile"}
              w={viewMode === "mobile" ? "100%" : undefined}
            >
              {curIdx > 0 && (
                <Button
                  variant="neutral-secondary"
                  size="base"
                  onClick={handlePrevious}
                  fullWidth={viewMode === "mobile"}
                  miw={100}
                >
                  Previous
                </Button>
              )}
              <Button
                variant="primary"
                size="base"
                onClick={isLastPage ? handleFormSubmit : handleNext}
                fullWidth={viewMode === "mobile"}
                miw={100}
              >
                {isLastPage ? "Submit" : "Next"}
              </Button>
            </Group>
          </Stack>
        </ScrollArea>
        {showAssistant && (
          <AssistantDock>
            <AssistantPanel tenantId={tenantId} />
          </AssistantDock>
        )}
      </div>

      <Modal
        opened={!!sigModal}
        onClose={() => setSigModal(null)}
        title={<Title order={3}>Sign Here</Title>}
        size="lg"
        centered
      >
        {sigModal ? <SignatureCanvas key={sigModal} onConfirm={handleSignatureConfirm} /> : null}
      </Modal>
    </Flex>
  );
}
